'use client';

import { Icon } from '@/components/ui';
import { DETAIL_PAGE_LARGE_IMAGE_SIZE } from '@/lib/const';
import { useWishlist } from '@/hooks/useWishlist';
import { useStableImageLoadHandlers } from '@/hooks/useStableImageLoadHandlers';
import type { ProductImage } from '@/lib/product/types';
import { modalActions } from '@/states/modalState';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Keyboard, Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './ProductGallery.module.scss';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';

// Import PhotoSwipe styles
import 'photoswipe/style.css';

export interface ProductGalleryProps {
  images: ProductImage[];
  productName: string;
  className?: string;
  /** Unique gallery ID for PhotoSwipe (defaults to 'pdp-gallery') */
  galleryId?: string;
  /** Product description for share modal */
  productDescription?: string;
  /** Marketing badge (Limited, Boutique Exclusive, etc.) */
  badge?: string;
  /** Product SKU for wishlist functionality */
  productSku?: string;
  /** Product price amount for wishlist */
  productPrice?: number;
  /** Product currency for wishlist */
  productCurrency?: string;
  /** Product type (watch, strap, etc.) for wishlist */
  productType?: string;
  /** CTA type for GTM wishlist / pre-order dimensions */
  ctaType?: string;
}

/**
 * Product image gallery with Swiper carousel and PhotoSwipe lightbox
 *
 * Features:
 * - Swiper carousel with keyboard navigation
 * - Draggable scrollbar for progress indication
 * - Click on image opens fullscreen PhotoSwipe lightbox
 * - Zoom cursor on hover
 * - Wishlist and share icons
 *
 * Ported from breitling-storefront-next ImagesGallery/ImageCarousel components.
 */
export function ProductGallery({
  images,
  productName,
  className,
  galleryId = 'pdp-gallery',
  productDescription,
  badge,
  productSku,
  productPrice,
  productCurrency = 'USD',
  productType = 'watch',
  ctaType,
}: ProductGalleryProps) {
  const t = useTranslations('common');
  const swiperRef = useRef<SwiperType | null>(null);
  const lightboxRef = useRef<PhotoSwipeLightbox | null>(null);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [loadedUrls, setLoadedUrls] = useState<Set<string>>(() => new Set());

  const markImageLoaded = useCallback((url: string) => {
    setLoadedUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);
  const imageLoadHandlers = useStableImageLoadHandlers(markImageLoaded);

  // Wishlist functionality - defer check until after hydration to avoid mismatch
  const { isInWishlist, toggleItem } = useWishlist();
  const [inWishlist, setInWishlist] = useState(false);

  // Use ref to avoid isInWishlist causing infinite loops in useEffect
  const isInWishlistRef = useRef(isInWishlist);
  isInWishlistRef.current = isInWishlist;

  // Check wishlist status after mount to avoid hydration mismatch
  useEffect(() => {
    if (productSku) {
      setInWishlist(isInWishlistRef.current(productSku));
    }
  }, [productSku]);

  // Initialize PhotoSwipe lightbox
  useEffect(() => {
    if (images.length === 0) return;

    lightboxRef.current = new PhotoSwipeLightbox({
      gallery: `#${galleryId}`,
      children: 'a',
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      showHideAnimationType: 'fade',
    });

    // Set white background for lightbox
    lightboxRef.current.on('uiRegister', function () {
      lightboxRef.current?.pswp?.element?.style.setProperty('--pswp-bg', '#ffffff');
    });

    lightboxRef.current.init();
    setIsReady(true);

    return () => {
      lightboxRef.current?.destroy();
      lightboxRef.current = null;
    };
  }, [galleryId, images.length]);

  // Update Swiper navigation refs after mount
  useEffect(() => {
    if (swiperRef.current && prevRef.current && nextRef.current) {
      // Re-assign navigation elements after they're rendered
      const swiper = swiperRef.current;
      if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
        swiper.params.navigation.prevEl = prevRef.current;
        swiper.params.navigation.nextEl = nextRef.current;
      }
      swiper.navigation.destroy();
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [isReady]);

  const handleWishlistClick = useCallback(() => {
    if (!productSku) {
      console.warn('[ProductGallery] Cannot toggle wishlist: missing productSku');
      return;
    }
    // Optimistically update local state for immediate feedback
    setInWishlist((prev) => !prev);
    toggleItem({
      sku: productSku,
      price: productPrice || 0,
      theCurrency: productCurrency,
      type: productType,
      ctaType,
      itemListName: 'PDP',
    });
  }, [productSku, productPrice, productCurrency, productType, ctaType, toggleItem]);

  const handleShareClick = useCallback(() => {
    // Open share modal with product info
    modalActions.open('share', {
      url: typeof window !== 'undefined' ? window.location.href : '',
      title: productName,
      description: productDescription,
      image: images[0]?.url,
    });
  }, [productName, productDescription, images]);

  if (!images.length) return null;

  const hasMultipleImages = images.length > 1;

  return (
    <div className={clsx(styles.gallery, className)}>
      {/* Action Icons */}
      <div className={styles.galleryIcons}>
        <div className={styles.labelSlot}>
          {badge && <span className={styles.badge}>{badge}</span>}
        </div>
        <div className={styles.actionsWrapper}>
          <button
            type="button"
            className={clsx(styles.actionButton, inWishlist && styles.actionButtonActive)}
            onClick={handleWishlistClick}
            aria-label={inWishlist ? t('removeFromWishlist') : t('addToWishlist')}
          >
            <Icon name={inWishlist ? 'heart-filled' : 'heart-outline'} size={26} />
          </button>
          <button
            type="button"
            className={styles.actionButton}
            onClick={handleShareClick}
            aria-label={t('share.title')}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="1.5" />
              <line
                x1="8.59"
                y1="13.51"
                x2="15.42"
                y2="17.49"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <line
                x1="15.41"
                y1="6.51"
                x2="8.59"
                y2="10.49"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Swiper Carousel with PhotoSwipe */}
      <div id={galleryId} className={styles.swiperContainer}>
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          modules={[Keyboard, Navigation, Scrollbar, A11y]}
          keyboard={{ enabled: true, onlyInViewport: false }}
          loop={hasMultipleImages}
          scrollbar={{
            draggable: true,
            hide: !hasMultipleImages,
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          className={styles.swiper}
        >
          {images.map((image, index) => {
            const isLoaded = loadedUrls.has(image.url);
            const zoomUrl = image.largeUrl ?? image.url;
            const zoomWidth = image.width ?? DETAIL_PAGE_LARGE_IMAGE_SIZE;
            const zoomHeight = image.height ?? DETAIL_PAGE_LARGE_IMAGE_SIZE;

            return (
              <SwiperSlide key={image.url || index} className={styles.slide}>
                <a
                  href={zoomUrl}
                  data-pswp-width={zoomWidth}
                  data-pswp-height={zoomHeight}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.imageLink}
                  onClick={(e) => {
                    // PhotoSwipe handles the click, prevent default navigation
                    e.preventDefault();
                  }}
                >
                  {!isLoaded ? (
                    <span className={styles.imageLoading} aria-hidden="true">
                      <Icon name="spinner" size={32} className={styles.imageSpinner} />
                    </span>
                  ) : null}
                  <Image
                    src={image.url}
                    alt={image.alt || `${productName} - Image ${index + 1}`}
                    width={680}
                    height={680}
                    className={clsx(styles.image, isLoaded && styles.imageLoaded)}
                    sizes="(max-width: 991px) 100vw, 680px"
                    priority={index === 0}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    data-image-url={image.url}
                    ref={imageLoadHandlers.ref}
                    onLoad={imageLoadHandlers.onLoad}
                    onError={imageLoadHandlers.onError}
                  />
                </a>
              </SwiperSlide>
            );
          })}

          {/* Navigation Arrows */}
          {hasMultipleImages && (
            <>
              <button
                ref={prevRef}
                type="button"
                className={clsx(styles.navButton, styles.navPrev)}
                aria-label={t('previousImage')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 39.997 39.997">
                  <rect fill="none" data-name="Rectangle 24560"></rect>
                  <path
                    fill="#fff"
                    d="m27.946 3.334.982.969-15.9 15.691 15.9 15.7-.982.973-16.878-16.672Z"
                    data-name="Path 5630"
                  ></path>
                </svg>
              </button>
              <button
                ref={nextRef}
                type="button"
                className={clsx(styles.navButton, styles.navNext)}
                aria-label={t('nextImage')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 39.997 39.997">
                  <rect fill="inherit"></rect>
                  <path
                    fill="inherit"
                    d="m12.05 36.666-.982-.969 15.9-15.691-15.9-15.7.982-.973 16.878 16.672Z"
                  ></path>
                </svg>
              </button>
            </>
          )}
        </Swiper>
      </div>
    </div>
  );
}

ProductGallery.displayName = 'ProductGallery';
